/**
 * DoctorPhotoTile — 4:3 rectangular photo tile with an elegant initials
 * fallback for doctors who haven't uploaded a portrait yet.
 *
 * Used on Find-a-Doctor cards, BookAppointment hero, and any surface
 * that displays a doctor's photo in a card-style crop. Mirrors the
 * `DoctorAvatar` component (circular) but preserves the 4:3 aspect
 * ratio so it slots into card layouts without cropping surrounding
 * elements.
 *
 * Fallback design:
 *   - Full-tile gradient background derived from the doctor's name so
 *     each card feels unique (5 palette variants, deterministic).
 *   - Large initials (font-display, tracked) centered.
 *   - No emoji, no stock avatars — clean, minimal, professional.
 *
 * Props:
 *   src              — image URL. When null/missing OR onError, renders fallback.
 *   name             — doctor's full name (used for initials + palette pick).
 *   className        — extra utility classes for the container.
 *   testid           — optional data-testid.
 */
import React, { useState } from "react";
import { Sparkle } from "@phosphor-icons/react";

const initialsOf = (name) =>
    (name || "")
        .replace(/^(dr\.?|doctor)\s+/i, "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";

// Deterministic palette pick — same name always gets the same gradient.
// Palette leans on Invisique's teal/cyan spectrum with subtle warm accents
// so cards feel varied but stay on-brand.
const PALETTES = [
    "bg-gradient-to-br from-teal-400 to-cyan-600",
    "bg-gradient-to-br from-cyan-500 to-blue-600",
    "bg-gradient-to-br from-emerald-400 to-teal-600",
    "bg-gradient-to-br from-sky-400 to-indigo-600",
    "bg-gradient-to-br from-teal-500 to-emerald-600",
];

const hashOf = (s) => {
    let h = 0;
    for (let i = 0; i < (s || "").length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
};

export function DoctorPhotoTile({ src, name, className = "", testid }) {
    const [broken, setBroken] = useState(false);
    const showImg = !!src && !broken;

    if (showImg) {
        return (
            <div className={`aspect-[4/3] overflow-hidden ${className}`} data-testid={testid}>
                <img
                    src={src}
                    alt={name || "Doctor"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    onError={() => setBroken(true)}
                    draggable={false}
                />
            </div>
        );
    }

    const palette = PALETTES[hashOf(name || "") % PALETTES.length];
    return (
        <div
            className={`relative aspect-[4/3] ${palette} grid place-items-center text-white overflow-hidden ${className}`}
            data-testid={testid}
        >
            <div className="text-center">
                <div className="font-display text-5xl font-bold tracking-tight drop-shadow-sm">
                    {initialsOf(name)}
                </div>
            </div>
            {/* iter100.257 — Subtle Invisique™ sparkle watermark. Renders on
                the fallback tile only (never over real doctor photos) so
                every placeholder still quietly carries the brand. Low
                opacity + bottom-right position keeps it noise-free. */}
            <Sparkle
                size={24}
                weight="fill"
                className="absolute bottom-3 right-3 text-white/25 pointer-events-none select-none"
                aria-hidden="true"
            />
        </div>
    );
}

export default DoctorPhotoTile;
